/**
 * Legalities Section - Format legality status pills
 *
 * Shared component for displaying format legality.
 * Used by both collection and deck editor card details panels.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { IoCheckmark, IoChevronDown, IoChevronUp, IoClose, IoWarning } from 'react-icons/io5';

import type { Format, Legality, ScryfallCard } from '@/types/card';

export interface LegalitiesSectionProps {
  card: ScryfallCard;
}

/** Primary formats to always show */
const PRIMARY_FORMATS: Format[] = ['standard', 'pioneer', 'modern', 'legacy', 'vintage', 'commander', 'pauper'];

/** Format display names */
const FORMAT_LABELS: Partial<Record<Format, string>> = {
  standard: 'Standard',
  pioneer: 'Pioneer',
  modern: 'Modern',
  legacy: 'Legacy',
  vintage: 'Vintage',
  commander: 'Commander',
  pauper: 'Pauper',
  historic: 'Historic',
  timeless: 'Timeless',
  explorer: 'Explorer',
  brawl: 'Brawl',
  alchemy: 'Alchemy',
  oathbreaker: 'Oathbreaker',
  duel: 'Duel Cmdr',
  oldschool: 'Old School',
  premodern: 'Premodern',
  penny: 'Penny',
};

interface LegalityPillProps {
  format: string;
  legality: Legality;
}

function LegalityPill({ format, legality }: LegalityPillProps): ReactElement {
  const label = FORMAT_LABELS[format as Format] ?? format;

  const config = {
    legal: {
      bg: 'bg-success/15',
      text: 'text-success',
      icon: <IoCheckmark className="h-3 w-3" />,
    },
    restricted: {
      bg: 'bg-gold/15',
      text: 'text-gold',
      icon: <IoWarning className="h-3 w-3" />,
    },
    banned: {
      bg: 'bg-danger/15',
      text: 'text-danger',
      icon: <IoClose className="h-3 w-3" />,
    },
    not_legal: {
      bg: 'bg-slate',
      text: 'text-ash',
      icon: null,
    },
  }[legality];

  return (
    <span className={clsx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium', config.bg, config.text)}>
      {config.icon}
      {label}
    </span>
  );
}

export default function LegalitiesSection({ card }: LegalitiesSectionProps): ReactElement {
  const [showAll, setShowAll] = useState(false);

  const legalities = card.legalities;

  // Get primary and secondary formats
  const primaryLegalities = PRIMARY_FORMATS.map((format) => ({
    format,
    legality: legalities[format],
  })).filter((item) => item.legality);

  const secondaryFormats = Object.keys(legalities).filter(
    (format) => !PRIMARY_FORMATS.includes(format as Format) && legalities[format as Format] !== 'not_legal',
  ) as Format[];

  const hasSecondary = secondaryFormats.length > 0;

  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <h3 className="text-ash mb-4 text-xs font-medium tracking-widest uppercase">Legalities</h3>

        {/* Primary Formats */}
        <div className="flex flex-wrap gap-2">
          {primaryLegalities.map(({ format, legality }) => (
            <LegalityPill key={format} format={format} legality={legality} />
          ))}
        </div>

        {/* Secondary Formats (expandable) */}
        {hasSecondary && (
          <>
            {showAll && (
              <div className="border-storm mt-3 flex flex-wrap gap-2 border-t pt-3">
                {secondaryFormats.map((format) => (
                  <LegalityPill key={format} format={format} legality={legalities[format]} />
                ))}
              </div>
            )}

            <button
              onClick={() => setShowAll(!showAll)}
              className="text-ash hover:text-silver mt-3 flex items-center gap-1 text-xs transition-colors"
            >
              {showAll ? (
                <>
                  <IoChevronUp className="h-3 w-3" />
                  Show less
                </>
              ) : (
                <>
                  <IoChevronDown className="h-3 w-3" />
                  Show {secondaryFormats.length} more formats
                </>
              )}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
